import type { UserFormSubmitPayload, useNotificationSnackbar } from "design-components";
import { getThunkErrorMessage } from "design-components";
import type { ApiCustomerDetail, InviteCustomerUserInput } from "api-client";
import { shouldSuppressApiErrorNotification } from "api-client";
import { useCallback, useMemo, useRef, useState } from "react";
import type { CustomerDetailOperatorActions } from "design-components";
import { useNavigate } from "react-router-dom";
import type { AppDispatch } from "../../../shared/store/store";
import {
  deleteCustomer,
  inviteCustomerUser,
  updateCustomerStatus,
} from "../../../shared/store/customerDetail";
import { resolveCustomerOperatorId } from "../utils/customer-detail.page.utils";
import {
  buildCustomerDeactivatedNotification,
  buildCustomerDeleteConfirmCopy,
  resolveActiveBookingsCount,
  resolveCustomerDisplayName,
} from "../utils/customer-delete-confirm.copy";
import { parseCanonicalCustomerId } from "../utils/customer-delete-confirm.helpers";

type ShowNotification = ReturnType<typeof useNotificationSnackbar>["showNotification"];
type StatusConfirmMode = "activate" | "deactivate";

interface UseCustomerDetailOperatorActionsParams {
  customerId: string | undefined;
  customer: ApiCustomerDetail | null;
  customerIsActive: boolean;
  dispatch: AppDispatch;
  showNotification: ShowNotification;
  onAssociateLocation: () => void;
  onEditLocation: (locationId: number) => void;
  onDeleteLocation: (locationId: number) => void;
}

export function useCustomerDetailOperatorActions({
  customerId,
  customer,
  customerIsActive,
  dispatch,
  showNotification,
  onAssociateLocation,
  onEditLocation,
  onDeleteLocation,
}: UseCustomerDetailOperatorActionsParams) {
  const navigate = useNavigate();
  const [createBookingOpen, setCreateBookingOpen] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusConfirmMode, setStatusConfirmMode] = useState<StatusConfirmMode>("deactivate");
  const [statusConfirmError, setStatusConfirmError] = useState<string | null>(null);
  const [inviteUserDrawerOpen, setInviteUserDrawerOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmError, setDeleteConfirmError] = useState<string | null>(null);
  const [deleteConfirmSubmitting, setDeleteConfirmSubmitting] = useState(false);
  const deleteConfirmSubmittingRef = useRef(false);

  const openStatusConfirm = useCallback((mode: StatusConfirmMode) => {
    setStatusConfirmMode(mode);
    setStatusConfirmError(null);
    setStatusConfirmOpen(true);
  }, []);

  const handleActivateClick = useCallback(() => {
    openStatusConfirm("activate");
  }, [openStatusConfirm]);

  const handleDeactivateClick = useCallback(() => {
    openStatusConfirm("deactivate");
  }, [openStatusConfirm]);

  const handleDeleteCustomerClick = useCallback(() => {
    setDeleteConfirmError(null);
    setDeleteConfirmOpen(true);
  }, []);

  const handleStatusConfirmClose = useCallback(() => {
    setStatusConfirmOpen(false);
    setStatusConfirmError(null);
  }, []);

  const handleDeleteConfirmClose = useCallback(() => {
    if (deleteConfirmSubmitting) return;
    setDeleteConfirmOpen(false);
    setDeleteConfirmError(null);
  }, [deleteConfirmSubmitting]);

  const handleStatusConfirm = useCallback(async () => {
    if (!customerId || !customer) return;
    const operatorId = resolveCustomerOperatorId(customer);
    const nextStatus = statusConfirmMode === "activate" ? "Active" : "Inactive";
    setStatusConfirmError(null);
    try {
      await dispatch(
        updateCustomerStatus({ customerId, operatorId, status: nextStatus }),
      ).unwrap();
      handleStatusConfirmClose();
      if (statusConfirmMode === "activate") {
        showNotification({
          variant: "success",
          title: "Customer activated",
          description: `${customer.name} is now active.`,
        });
      } else {
        showNotification(buildCustomerDeactivatedNotification(customer.name));
      }
    } catch (e: unknown) {
      setStatusConfirmError(getThunkErrorMessage(e, "Failed to update customer status."));
    }
  }, [customer, customerId, dispatch, handleStatusConfirmClose, showNotification, statusConfirmMode]);

  const activeBookingsCount = resolveActiveBookingsCount(customer?.activeBookingsCount);

  const deleteConfirmCopy = useMemo(() => {
    const companyName = resolveCustomerDisplayName(
      customer?.companyLegalName,
      customer?.name,
    );
    return buildCustomerDeleteConfirmCopy({ companyName, activeBookingsCount });
  }, [activeBookingsCount, customer?.companyLegalName, customer?.name]);

  /** Already Inactive + open bookings → Deactivate CTA must stay disabled (AV-53). */
  const deleteConfirmPrimaryDisabled =
    deleteConfirmSubmitting || (activeBookingsCount > 0 && !customerIsActive);

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteConfirmSubmittingRef.current) return;

    if (!customerId || !customer) {
      setDeleteConfirmError("Customer is not available.");
      return;
    }

    const ids = parseCanonicalCustomerId(customerId, customer.customerId);
    if (!ids.ok) {
      setDeleteConfirmError(ids.error);
      return;
    }

    if (activeBookingsCount > 0 && !customerIsActive) {
      setDeleteConfirmError("This customer is already inactive and has active bookings.");
      return;
    }

    const operatorId = resolveCustomerOperatorId(customer);
    setDeleteConfirmError(null);
    deleteConfirmSubmittingRef.current = true;
    setDeleteConfirmSubmitting(true);
    try {
      if (activeBookingsCount > 0) {
        await dispatch(
          updateCustomerStatus({
            customerId: ids.customerIdStr,
            operatorId,
            status: "Inactive",
          }),
        ).unwrap();
        setDeleteConfirmOpen(false);
        showNotification(buildCustomerDeactivatedNotification(customer.name));
      } else {
        await dispatch(
          deleteCustomer({ customerId: ids.customerId, operatorId }),
        ).unwrap();
        setDeleteConfirmOpen(false);
        const companyLabel = resolveCustomerDisplayName(
          customer.companyLegalName,
          customer.name,
        );
        navigate("/customers", {
          replace: true,
          state: {
            flashNotification: {
              variant: "success" as const,
              title: "Customer deleted",
              description: `${companyLabel} has been removed.`,
            },
          },
        });
      }
    } catch (e: unknown) {
      setDeleteConfirmError(
        getThunkErrorMessage(
          e,
          activeBookingsCount > 0
            ? "Could not deactivate customer."
            : "Could not delete customer.",
        ),
      );
    } finally {
      deleteConfirmSubmittingRef.current = false;
      setDeleteConfirmSubmitting(false);
    }
  }, [
    activeBookingsCount,
    customer,
    customerId,
    customerIsActive,
    dispatch,
    navigate,
    showNotification,
  ]);

  const statusConfirmTitle =
    statusConfirmMode === "activate" ? "Activate customer" : "Deactivate customer";

  const statusConfirmDescription = useMemo(() => {
    const name = customer?.name?.trim() || "this customer";
    if (statusConfirmMode === "activate") {
      return <>Are you sure you want to activate {name}? They will be able to use the platform again.</>;
    }
    return <>Are you sure you want to deactivate {name}? They will lose access until reactivated.</>;
  }, [customer?.name, statusConfirmMode]);

  const statusConfirmPrimaryLabel = statusConfirmMode === "activate" ? "Activate" : "Deactivate";

  const handleCreateBooking = useCallback(() => {
    if (!customerIsActive) return;
    setCreateBookingOpen(true);
  }, [customerIsActive]);

  const handleCloseCreateBooking = useCallback(() => {
    setCreateBookingOpen(false);
  }, []);

  const handleAddUser = useCallback(() => {
    if (!customerIsActive) return;
    setInviteUserDrawerOpen(true);
  }, [customerIsActive]);

  const handleInviteUserDrawerClose = useCallback(() => {
    setInviteUserDrawerOpen(false);
  }, []);

  const handleInviteUserSubmit = useCallback(
    async (payload: UserFormSubmitPayload) => {
      if (!customerId || !customer || !customerIsActive) return;
      const operatorId = resolveCustomerOperatorId(customer);
      const body: InviteCustomerUserInput = {
        emailId: payload.emailId,
        firstName: payload.firstName,
        roleName: payload.roleName,
        contactNumber: payload.contactNumber,
        address: null,
        redirectUrl: null,
        notify: true,
        ignoreExisting: null,
        expiresInDays: null,
      };
      try {
        const invitedUser = await dispatch(
          inviteCustomerUser({ customerId, operatorId, body }),
        ).unwrap();
        handleInviteUserDrawerClose();
        const userName =
          invitedUser.fullName.trim() || payload.firstName.trim() || payload.emailId;
        const customerName = resolveCustomerDisplayName(null, customer.name, "this customer");
        showNotification({
          variant: "success",
          title: "Invitation sent",
          description: `${userName} has been invited to ${customerName} and will receive an email.`,
        });
      } catch (e: unknown) {
        if (shouldSuppressApiErrorNotification(e)) {
          return;
        }
        showNotification({
          variant: "error",
          title: "Invite failed",
          description: getThunkErrorMessage(e, "Failed to invite user."),
        });
      }
    },
    [customer, customerIsActive, customerId, dispatch, handleInviteUserDrawerClose, showNotification],
  );

  const operatorActions = useMemo<CustomerDetailOperatorActions>(
    () => ({
      onCreateBooking: handleCreateBooking,
      onAssociateLocation,
      onAddUser: handleAddUser,
      customerIsActive,
      onActivate: handleActivateClick,
      onDeactivate: handleDeactivateClick,
      onDeleteCustomer: handleDeleteCustomerClick,
      onEditLocation,
      onDeleteLocation,
    }),
    [
      customerIsActive,
      handleActivateClick,
      handleAddUser,
      handleCreateBooking,
      handleDeactivateClick,
      handleDeleteCustomerClick,
      onAssociateLocation,
      onDeleteLocation,
      onEditLocation,
    ],
  );

  return {
    createBookingOpen,
    statusConfirmOpen,
    statusConfirmError,
    statusConfirmTitle,
    statusConfirmDescription,
    statusConfirmPrimaryLabel,
    deleteConfirmOpen,
    deleteConfirmError,
    deleteConfirmSubmitting,
    deleteConfirmPrimaryDisabled,
    deleteConfirmCopy,
    inviteUserDrawerOpen,
    operatorActions,
    handleCloseCreateBooking,
    handleStatusConfirmClose,
    handleStatusConfirm,
    handleDeleteConfirmClose,
    handleDeleteConfirm,
    handleInviteUserDrawerClose,
    handleInviteUserSubmit,
  };
}
